import { useUploadStore } from "@components/FileUpload/uploadStore";
import { useNotification } from "@context/NotificationContext";
import type { DepartureResponse } from "@services/departure/types";
import { resubmitDepartureRequest } from "@services/departureServiceApi";
import type { Task } from "@services/vessel-call/types";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import type { DepartureAvailability } from "../Departure/availability/departureAvailability";
import { FORM_FIELDS } from "../Departure/constants";
import mapRequestDepartureFormToPayload from "../Departure/mappers/requestDepartureMapper";
import { useRequestDepartureFormStore } from "../Departure/store/useRequestDepartureForm.store";

type useResubmitDepartureRequestProps = {
  vesselCallId: string;
  task: Task;
  status: string;
  resetForm: () => void;
  refetchDepartureDetails?: () => void;
  vesselId: string;
  submittedBy: string;
  submittedByRole: string;
  initialCustomsClearance?: { name: string; url: string };
  refetchDepartureAvailability?: () => Promise<DepartureAvailability | undefined>;
};

const LANG_BASE = "vessel-call.request-departure.notifications";

export const useResubmitDepartureRequest = (props: useResubmitDepartureRequestProps) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { clearFilesForKey } = useUploadStore();

  const mutation = useMutation({
    mutationFn: (payload: DepartureResponse) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "RequestDeparture",
          action: "resubmit_departure_request",
          entityType: "departure_request",
        },
        (transactionId) =>
          resubmitDepartureRequest(
            payload,
            props?.vesselCallId,
            ...(transactionId ? [transactionId] : []),
          ),
      ),
    onSuccess: () => {
      props.resetForm();
      clearFilesForKey(FORM_FIELDS.PRE_SAILING_MASTER_DECLARATION);
      clearFilesForKey(FORM_FIELDS.DEPARTURE_CREW_LIST);
      clearFilesForKey(FORM_FIELDS.CARGO_MANIFEST);
      clearFilesForKey(FORM_FIELDS.CUSTOMS_CLEARANCE);
      clearFilesForKey(FORM_FIELDS.ADDITIONAL_DOCUMENTS);
      notify({
        title: t(`${LANG_BASE}.request-submitted-success-title`),
        description: t(`${LANG_BASE}.request-submitted-success-description`),
        type: "success",
      });
      props.refetchDepartureDetails?.();
    },
    onError: (err: any) => {
      void props.refetchDepartureAvailability?.();
      const errorData = err?.response?.data;
      const isServiceUnavailable = errorData?.status === 503;

      notify({
        title: t(`${LANG_BASE}.request-submitted-error-title`),
        description:
          (isServiceUnavailable ? errorData?.detail : errorData?.message) ??
          err?.message ??
          t(`${LANG_BASE}.request-submitted-error-description`),
        type: "error",
        autoHideDuration: isServiceUnavailable ? 30000 : undefined,
      });

      if (isServiceUnavailable) {
        props.refetchDepartureDetails && props.refetchDepartureDetails();
      }
    },
  });

  const onSubmit = async () => {
    const state = useRequestDepartureFormStore.getState();
    const payload = {
      ...mapRequestDepartureFormToPayload(
        props?.vesselCallId,
        state,
        props?.initialCustomsClearance,
      ),
      task: props.task,
      status: props.status,
      vesselId: props.vesselId,
      submittedBy: props.submittedBy,
      submittedByRole: props.submittedByRole,
    };
    mutation.mutate(payload);
  };

  return { onSubmit };
};
